import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { listPushSubscriptions } from '@/api/notifications'
import { PageHeader } from '@/components/layout/PageHeader'
import { Loader } from '@/components/ui/Loader'
import { Button } from '@/components/ui/Button'
import { SearchInput } from '@/components/ui/SearchInput'
import { PillToggle } from '@/components/ui/PillToggle'
import { SegmentedControl } from '@/components/ui/SegmentedControl'
import { emptyTableCellClassName } from '@/constants/ui'
import { formatMoscowDateTime } from '@/utils/format'
import { USE_MOCK } from '@/config'
import { NOTIFICATION_ROLES, notificationRoleLabel } from '@/pages/notifications/i18n/notification_roles'
import { roleLabelForTemplate } from '@/utils/notification-ui'

export function PushSubscriptionsPage() {
  const { t } = useTranslation()
  const [page, setPage] = useState(1)
  const [roleFilter, setRoleFilter] = useState<Set<string>>(() => new Set())
  const [platform, setPlatform] = useState<'all' | 'ios' | 'android'>('all')
  const [staleOnly, setStaleOnly] = useState(false)
  const [search, setSearch] = useState('')

  const roleList = useMemo(() => {
    if (roleFilter.size === 0) return []
    return [...roleFilter]
  }, [roleFilter])

  const { data, isLoading, error } = useQuery({
    queryKey: ['push-subscriptions', page, roleList, platform, staleOnly, search],
    queryFn: () =>
      listPushSubscriptions({
        page,
        roleFilter: roleList,
        platform,
        staleOnly,
        search,
      }),
  })

  const toggleRole = (r: string) => {
    setRoleFilter((prev) => {
      const n = new Set(prev)
      if (n.has(r)) n.delete(r)
      else n.add(r)
      return n
    })
    setPage(1)
  }

  const summary = data?.summary

  return (
    <div>
      <PageHeader title={t('notif.push.title')} subtitle={t('notif.push.subtitle')} />
      {USE_MOCK ? (
        <p className="mb-4 text-sm text-[color:var(--stryzh-color-text-secondary)]">
          {t('notif.push.mockHint')}
        </p>
      ) : null}
      {error ? (
        <div
          role="alert"
          className="mb-3 rounded-md border border-[color:var(--stryzh-color-status-error)] px-3 py-2 text-sm text-[color:var(--stryzh-color-status-error)]"
        >
          {(error as Error).message}
        </div>
      ) : null}

      {summary ? (
        <div className="mb-6 grid [grid-template-columns:repeat(auto-fill,minmax(11rem,1fr))] gap-3">
          {[
            { label: t('notif.push.usersWithTokens'), value: summary.usersWithTokens },
            { label: t('notif.push.totalTokens'), value: summary.totalTokens },
            { label: 'iOS', value: summary.iosTokens },
            { label: 'Android', value: summary.androidTokens },
            { label: t('notif.push.staleUsers'), value: summary.staleUsers },
          ].map((c) => (
            <div
              key={c.label}
              className="rounded-xl border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)] px-4 py-3"
            >
              <div className="text-2xl font-semibold tabular-nums text-[color:var(--stryzh-color-text-primary)]">
                {c.value}
              </div>
              <div className="mt-1 text-xs leading-snug text-[color:var(--stryzh-color-text-secondary)]">{c.label}</div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="mb-4 divide-y divide-[color:var(--stryzh-color-border-default)] rounded-xl border border-[color:var(--stryzh-color-border-default)] bg-[color:var(--stryzh-color-surface)]">
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
          <SearchInput
            className="max-w-none sm:max-w-md md:max-w-lg"
            placeholder={t('notif.push.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onBlur={() => setPage(1)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setPage(1)
            }}
          />
          <PillToggle
            className="shrink-0 self-start sm:self-center"
            checked={staleOnly}
            onChange={(v) => {
              setStaleOnly(v)
              setPage(1)
            }}
            label={t('notif.push.staleOnly')}
          />
        </div>
        <div className="grid gap-5 p-4 lg:grid-cols-[minmax(0,14rem)_1fr] lg:items-start lg:gap-8">
          <div className="space-y-2">
            <div className="text-xs font-medium text-[color:var(--stryzh-color-text-secondary)]">{t('notif.push.platform')}</div>
            <div className="w-full max-w-[13.5rem]">
              <SegmentedControl
                aria-label={t('notif.push.platform')}
                value={platform}
                onChange={(p) => {
                  setPlatform(p)
                  setPage(1)
                }}
                options={[
                  { value: 'all' as const, label: t('notif.push.platformAll') },
                  { value: 'ios' as const, label: 'iOS' },
                  { value: 'android' as const, label: 'Android' },
                ]}
              />
            </div>
          </div>
          <div className="min-w-0 space-y-2">
            <div className="text-xs font-medium text-[color:var(--stryzh-color-text-secondary)]">
              {t('notif.push.roleFilterHint')}
            </div>
            <div className="flex flex-wrap gap-x-2 gap-y-2.5">
              {NOTIFICATION_ROLES.map((r) => (
                <button
                  key={r}
                  type="button"
                  className={clsx(
                    'stryzh-filter-chip',
                    roleFilter.has(r) && 'stryzh-filter-chip--selected',
                  )}
                  onClick={() => toggleRole(r)}
                >
                  {notificationRoleLabel(r)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[color:var(--stryzh-color-border-default)]">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead className="bg-[color:var(--stryzh-color-surface)] text-[color:var(--stryzh-color-text-secondary)]">
            <tr>
              <th className="px-3 py-2">{t('notif.push.colUser')}</th>
              <th className="px-3 py-2">{t('notif.push.colPlatforms')}</th>
              <th className="px-3 py-2">{t('notif.push.colVersions')}</th>
              <th className="px-3 py-2">{t('notif.push.colTokens')}</th>
              <th className="px-3 py-2">{t('notif.push.colLastSeen')}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={5} className={emptyTableCellClassName}>
                  <Loader size="sm" centered label={t('common.loading')} />
                </td>
              </tr>
            ) : (data?.items.length ?? 0) === 0 ? (
              <tr>
                <td colSpan={5} className={emptyTableCellClassName}>
                  {t('common.noData')}
                </td>
              </tr>
            ) : (
              data!.items.map((r) => (
                <tr key={r.user_id} className="border-t border-[color:var(--stryzh-color-border-default)]">
                  <td className="px-3 py-2">
                    <Link className="text-[color:var(--stryzh-color-accent-orange)] hover:underline" to={`/users/${r.user_id}`}>
                      {r.full_name ?? r.user_id.slice(0, 8)}
                    </Link>
                    <div className="text-xs text-[color:var(--stryzh-color-text-secondary)]">
                      {r.role ? roleLabelForTemplate(r.role) : ''}
                      {r.stale ? (
                        <span className="ml-2 rounded border border-amber-700/50 bg-amber-950/40 px-1.5 py-0.5 text-amber-200">
                          {t('notif.push.inactive')}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {r.platforms.map((p) => (
                        <span key={p} className="rounded border border-[color:var(--stryzh-color-border-default)] px-1.5 text-xs uppercase">
                          {p}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="max-w-[160px] truncate px-3 py-2 text-xs">{r.app_versions.join(', ') || '—'}</td>
                  <td className="px-3 py-2">{r.token_count}</td>
                  <td className="px-3 py-2 text-xs">{r.last_seen_at ? formatMoscowDateTime(r.last_seen_at) : '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data && data.total > 50 ? (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button type="button" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
            {t('notif.common.prev')}
          </Button>
          <span className="text-sm text-[color:var(--stryzh-color-text-secondary)]">
            {t('notif.push.pageInfo', { page, pages: Math.max(1, Math.ceil(data.total / 50)), total: data.total })}
          </span>
          <Button
            type="button"
            variant="secondary"
            disabled={page * 50 >= data.total}
            onClick={() => setPage((p) => p + 1)}
          >
            {t('notif.common.next')}
          </Button>
        </div>
      ) : null}
    </div>
  )
}
